import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './shell/ErrorBoundary.js';
import { UpdateBanner } from './shell/UpdateBanner.js';
import { AppShell } from './shell/AppShell.js';
import { AccessPage } from './routes/Access.js';
import { TodayPage } from './routes/Today.js';
import { useAuth } from './lib/auth.js';

/**
 * Route lazy: mappa, scopri, budget, onboarding e invito non pesano sul bundle
 * iniziale della schermata Oggi (performance budget in DEPLOYMENT.md §8).
 */
const MapPage = lazy(async () => ({ default: (await import('./routes/MapView.js')).MapPage }));
const DiscoverPage = lazy(async () => ({
  default: (await import('./routes/Discover.js')).DiscoverPage,
}));
const BudgetPage = lazy(async () => ({
  default: (await import('./routes/Budget.js')).BudgetPage,
}));
const TripSectionPage = lazy(async () => ({
  default: (await import('./routes/TripSection.js')).TripSectionPage,
}));
const OnboardingWizard = lazy(async () => ({
  default: (await import('./features/onboarding/OnboardingWizard.js')).OnboardingWizard,
}));
const InviteAcceptPage = lazy(async () => ({
  default: (await import('./routes/InviteAccept.js')).InviteAcceptPage,
}));

function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const auth = useAuth();
  const location = useLocation();

  if (auth.isLoading) {
    return (
      <div role="status" aria-busy="true" className="p-8 text-center text-text-muted">
        <span className="sr-only">Verifica dell&apos;accesso in corso</span>
      </div>
    );
  }

  // Offline con sessione non verificabile: si dice, non si butta fuori l'utente
  // facendogli credere di essere stato disconnesso.
  if (auth.user === null && auth.isOffline) {
    return (
      <div className="p-8 text-center text-[15px] text-text-muted">
        Sei offline e non riesco a verificare il tuo accesso. Riprova quando torni in rete.
      </div>
    );
  }

  if (auth.user === null) {
    return <Navigate to={`/accedi?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Attesa mostrata mentre arriva il codice di una schermata caricata a richiesta.
 *
 * `role="status"` + `aria-busy`: chi usa uno screen reader sente che sta
 * caricando, invece di trovarsi davanti al silenzio.
 */
function CaricamentoSchermata(): ReactNode {
  return (
    <div role="status" aria-busy="true" className="flex min-h-dvh items-center justify-center p-8">
      <span className="sr-only">Caricamento in corso</span>
      <div className="h-8 w-8 animate-pulse rounded-full bg-surface-sunken" />
    </div>
  );
}

export function App(): ReactNode {
  return (
    /*
     * Il `Suspense` avvolge TUTTE le rotte, non solo quelle dentro AppShell.
     *
     * `/nuovo-viaggio` e `/invito/:token` stanno fuori dal guscio dell'app —
     * sono schermate a tutto schermo — ma i loro componenti sono caricati a
     * richiesta. Senza un contenitore di attesa, React smonta l'intero albero
     * quando il codice non e' ancora arrivato: schermo bianco, nessun messaggio
     * (errore #426). In sviluppo non si vedeva mai, perche' Vite serve i moduli
     * senza spezzarli e il caricamento e' istantaneo: si manifestava solo nella
     * build di produzione, cioe' solo agli utenti veri.
     *
     * L'`ErrorBoundary` e' la rete sotto: qualunque errore di rendering diventa
     * un messaggio leggibile invece di una pagina vuota.
     */
    <ErrorBoundary>
      <Suspense fallback={<CaricamentoSchermata />}>
        <AppRoutes />
      </Suspense>
      {/* Fuori dal Suspense: l'avviso di aggiornamento deve poter comparire
          anche mentre una schermata sta ancora caricando. */}
      <UpdateBanner />
    </ErrorBoundary>
  );
}

function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/accedi" element={<AccessPage mode="login" />} />
      <Route path="/registrati" element={<AccessPage mode="register" />} />
      <Route path="/invito/:token" element={<InviteAcceptPage />} />

      <Route
        path="/nuovo-viaggio"
        element={
          <RequireAuth>
            <OnboardingWizard />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/oggi" element={<TodayPage />} />
        <Route path="/mappa" element={<MapPage />} />
        <Route path="/scopri" element={<DiscoverPage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/viaggio" element={<TripSectionPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/oggi" replace />} />
      <Route path="*" element={<Navigate to="/oggi" replace />} />
    </Routes>
  );
}
